/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';

/**
 * Provides the API for service client registration implementations which are to
 * be provided with a reference to each service client (`AxiosInstance`) created.
 * The implementation can then monitor the client as needed, such as for outgoing
 * requests, incoming responses, transformations, globally applied configurations,
 * etc.
 */
export interface IAsyncClientRegistration {
  /**
   * Registers an async service client instance with the given implementation
   * so as to be included in the unified management of service client instances.
   *
   * @param instance The Service client which is to be managed by the given
   *        implementation.
   */
  register(instance: AxiosInstance);

  /**
   * Determines the number of clients which have been registered and returns
   * the result.
   *
   * @returns the number of service clients that have been registered.
   */
  getClientCount(): number;
}

/**
 * Provides a reference to the `IAsyncClientRegistration` instance which will be
 * provided with a reference to each service client created via `createAsyncClient`
 */
let registration: IAsyncClientRegistration;

/**
 * Allows for injecting a reference to the `IAsyncClientRegistration` implementation
 * which is to be used for automatically registering each service client instance
 * that is created via `createAsyncClient`. When a new service client is created,
 * the `register` method of the `IAsyncClientRegistration` instance will be invoked
 * with the client. The implementation can then monitor the client as needed, such
 * as for outgoing requests, incoming responses, transformations, globally applied
 * configurations, etc.
 *
 * ```typescript
 *   import {setAsyncClientRegister} from '@ion/core/src/services/AsyncClientManager';
 *   ...
 *
 *   const register = new ServiceClientRegistration();
 *   setAsyncClientRegister(register);
 *   // each time a new service client is created, it will be passed to the register
 *   // instance for subsequent observation and / or management ...
 * ```
 *
 * @param register an `IAsyncClientRegistration` instance that is to be provided
 *        with a reference to each service client created via `createAsyncClient`
 * @parma unset when true, allows for nullifying the currently set register. Note
 *        this argument should generally not be used and should only be used for
 *        use-cases where the register should be effectively removed.
 */
export const setAsyncClientRegister = (register: IAsyncClientRegistration, unset = false) => {
  if (!registration || unset) {
    registration = register;
  }
};

/**
 * Provides a convenience method for determining if an `IAsyncClientRegistration`
 * instance has already been set.
 *
 * ```typescript
 *   import {
 *     hasAsyncClientRegister,
 *     setAsyncClientRegister
 *   } from '@ion/core/src/services/AsyncClientManager';
 *   ...
 *
 *   if (!hasAsyncClientRegister()) {
 *     const register = new ServiceClientRegistration();
 *     setAsyncClientRegister(register);
 *   } else {
 *     ...
 *   }
 * ```
 *
 * @returns `true` if an `IAsyncClientRegistration` instance has already been set,
 *          otherwise, returns `false`.
 */
export const hasAsyncClientRegister = (): boolean => (
  !!registration
);

/**
 * Provides a convenience method for determining if the `IAsyncClientRegistration`
 * instance that has been set is the provided instance.
 *
 * ```typescript
 *   import {
 *     isAsyncClientRegister,
 *     setAsyncClientRegister
 *   } from '@ion/core/src/services/AsyncClientManager';
 *   ...
 *
 *   const register = new ServiceClientRegistration();
 *   if (!isAsyncClientRegister(register)) {
 *     setAsyncClientRegister(register);
 *   } else {
 *     ...
 *   }
 * ```
 *
 * @param   register An `IAsyncClientRegistration` instance to be verified.
 * @returns `true` if the `register` argument is the `IAsyncClientRegistration`
 *          instance that has been set,  otherwise `false`.
 */
export const isAsyncClientRegister = (register: IAsyncClientRegistration): boolean => (
  register === registration
);

/**
 * Provides a helper function from which service clients (`AxiosInstances`) are to
 * be created. All service modules are to use the helper exclusively to instantiate
 * their `AxiosInstance`, rather than doing so explicitly via `axios.create`. This
 * ensures that each service can be registered implicitly.
 *
 * ```typescript
 *   // ion/core/src/services/SomeService.ts
 *   import {createAsyncClient} from '@ion/core/src/services/AsyncClientManager';
 *   // or
 *   import {createAsyncClient} from './AsyncClientManager';
 *   ...
 *
 *   export const setConfig = (configuration: AxiosRequestConfig) => {
 *     config = {
 *       ...configuration
 *     };
 *     client = createAsyncClient(config);
 *     return client;
 *   };
 * ```
 *
 * @param   config The service configuration that is to be used when creating the
 *          client.
 * @param   withCredentials when `false`, will set `client.defaults.withCredentials`
 *          to `false`, otherwise it will be set to `true` (default).
 * @returns
 */
export const createAsyncClient = (config: AxiosRequestConfig, withCredentials = true): AxiosInstance => {
  const client = axios.create(config);
  client.defaults.withCredentials = withCredentials;
  registration?.register(client);
  return client;
};

export default createAsyncClient;
